const GUEST_CART_PREFIX = "leMemePrix_guestCart:";

function storageKey(restaurantId) {
  return `${GUEST_CART_PREFIX}${String(restaurantId)}`;
}

function newGuestItemId() {
  return `guest-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function computeGuestCartTotal(items) {
  return (items || []).reduce((sum, it) => {
    const line = Number(it.finalItemTotal || 0);
    if (Number.isFinite(line) && line > 0) return sum + line;
    const qty = Math.max(1, Number(it.quantity ?? 1));
    const base = Number(it.basePrice ?? 0);
    const extras = Number(it.extrasTotal ?? 0);
    const addons = Number(it.addonsTotal ?? 0);
    return sum + (base + extras + addons) * qty;
  }, 0);
}

function normalizeCart(cart, restaurantId) {
  const items = Array.isArray(cart?.items) ? cart.items : [];
  const totalPrice = computeGuestCartTotal(items);
  return {
    restaurantId: String(restaurantId),
    categoryType: cart?.categoryType || "general",
    items,
    totalPrice,
    wantsCutlery: cart?.wantsCutlery === true,
    chefNote: String(cart?.chefNote || "").trim(),
    status: "in-cart",
  };
}

export function getGuestCart(restaurantId) {
  if (!restaurantId) return null;
  try {
    const raw = localStorage.getItem(storageKey(restaurantId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return normalizeCart(parsed, restaurantId);
  } catch {
    return null;
  }
}

export function setGuestCart(restaurantId, cart) {
  if (!restaurantId) return null;
  const normalized = normalizeCart(cart || {}, restaurantId);
  try {
    localStorage.setItem(storageKey(restaurantId), JSON.stringify(normalized));
    localStorage.setItem("leMemePrix_lastCartRestaurantId", String(restaurantId));
  } catch {
    /* ignore */
  }
  return normalized;
}

export function clearGuestCart(restaurantId) {
  if (!restaurantId) return;
  try {
    localStorage.removeItem(storageKey(restaurantId));
  } catch {
    /* ignore */
  }
}

export function getGuestCartItemCount(restaurantId) {
  const cart = getGuestCart(restaurantId);
  if (!cart?.items?.length) return 0;
  return cart.items.reduce((n, it) => n + Math.max(1, Number(it.quantity ?? 1)), 0);
}

export function addGuestCartItems(restaurantId, categoryType, incomingItems) {
  const existing = getGuestCart(restaurantId) || { items: [], categoryType: categoryType || "general" };
  const items = [...(existing.items || [])];
  for (const item of incomingItems || []) {
    items.push({
      ...item,
      _id: item._id || newGuestItemId(),
      quantity: Math.max(1, Number(item.quantity ?? 1)),
    });
  }
  return setGuestCart(restaurantId, {
    ...existing,
    categoryType: categoryType || existing.categoryType || "general",
    items,
  });
}

export function updateGuestCartItem(restaurantId, cartItemId, nextItem) {
  const cart = getGuestCart(restaurantId);
  if (!cart) return null;
  const items = (cart.items || []).map((it) =>
    String(it._id) === String(cartItemId) ? { ...nextItem, _id: cartItemId } : it,
  );
  return setGuestCart(restaurantId, { ...cart, items });
}

export function updateGuestCartItemQty(restaurantId, cartItemId, quantity) {
  const cart = getGuestCart(restaurantId);
  if (!cart) return null;
  const qty = Math.max(1, Number(quantity) || 1);
  const items = (cart.items || []).map((it) => {
    if (String(it._id) !== String(cartItemId)) return it;
    const unit =
      qty > 0 && Number(it.finalItemTotal) > 0
        ? Number(it.finalItemTotal) / Math.max(1, Number(it.quantity ?? 1))
        : Number(it.basePrice ?? 0) + Number(it.extrasTotal ?? 0) + Number(it.addonsTotal ?? 0);
    return { ...it, quantity: qty, finalItemTotal: Math.round(unit * qty * 100) / 100 };
  });
  return setGuestCart(restaurantId, { ...cart, items });
}

export function removeGuestCartItem(restaurantId, cartItemId) {
  const cart = getGuestCart(restaurantId);
  if (!cart) return null;
  const items = (cart.items || []).filter((it) => String(it._id) !== String(cartItemId));
  return setGuestCart(restaurantId, { ...cart, items });
}

export function updateGuestCartPreferences(restaurantId, { wantsCutlery, chefNote } = {}) {
  const cart = getGuestCart(restaurantId);
  if (!cart) return null;
  return setGuestCart(restaurantId, {
    ...cart,
    ...(wantsCutlery !== undefined ? { wantsCutlery: wantsCutlery === true } : {}),
    ...(chefNote !== undefined ? { chefNote: String(chefNote || "").trim() } : {}),
  });
}

export function buildPayAtCounterCartPayload(cart, { loyaltyCheckoutPoints = 0 } = {}) {
  if (!cart?.items?.length) return null;
  return {
    categoryType: cart.categoryType || "general",
    wantsCutlery: cart.wantsCutlery === true,
    chefNote: cart.chefNote || "",
    activatedRestaurantOfferIds: cart.activatedRestaurantOfferIds || [],
    activatedSuperadminOfferId: cart.activatedSuperadminOfferId || null,
    offerFreeItemChoices: cart.offerFreeItemChoices || [],
    loyaltyCheckoutPoints: Math.max(0, Math.floor(Number(loyaltyCheckoutPoints) || 0)),
    cartItems: cart.items.map((it) => ({
      _id: it._id,
      menuItemId: it.menuItemId,
      name: it.name,
      description: it.description ?? "",
      basePrice: Number(it.basePrice ?? 0),
      quantity: Math.max(1, Number(it.quantity ?? 1)),
      selections: it.selections ?? {},
      selectedResolved: it.selectedResolved ?? it.selections?.selectedResolved ?? [],
      extras: it.extras ?? [],
      extrasTotal: Number(it.extrasTotal ?? 0),
      addonsTotal: Number(it.addonsTotal ?? 0),
      finalItemTotal: Number(it.finalItemTotal ?? 0),
      redeemWithPoints: !!it.redeemWithPoints,
      chefNote: it.chefNote ?? "",
    })),
  };
}

/** After login, push local guest cart lines to the server cart API. */
export async function mergeGuestCartToServer(api, restaurantId) {
  const guest = getGuestCart(restaurantId);
  if (!guest?.items?.length) return false;
  const payload = {
    restaurantId,
    categoryType: guest.categoryType || "general",
    items: guest.items.map((it) => ({
      menuItemId: it.menuItemId,
      name: it.name,
      description: it.description ?? "",
      basePrice: Number(it.basePrice ?? 0),
      quantity: Math.max(1, Number(it.quantity ?? 1)),
      selections: it.selections ?? {},
      selectedResolved: it.selectedResolved ?? it.selections?.selectedResolved ?? [],
      extras: it.extras ?? [],
      extrasTotal: Number(it.extrasTotal ?? 0),
      addonsTotal: Number(it.addonsTotal ?? 0),
      finalItemTotal: Number(it.finalItemTotal ?? 0),
      chefNote: it.chefNote ?? "",
    })),
  };
  await api.post("/api/cart/add", payload);
  if (guest.wantsCutlery || guest.chefNote) {
    await api.patch("/api/cart/preferences", {
      restaurantId,
      wantsCutlery: guest.wantsCutlery === true,
      chefNote: guest.chefNote || "",
    });
  }
  clearGuestCart(restaurantId);
  return true;
}

export const guestCounterSuccessStorageKey = (restaurantId) => `counterSuccessOrder:${restaurantId}`;
export const guestCounterTokenStorageKey = (restaurantId) => `counterSuccessGuestToken:${restaurantId}`;
